import React from 'react';

interface FlagIconProps {
  code: string;
  className?: string;
}

const FlagIcon: React.FC<FlagIconProps> = ({ code, className = '' }) => {
  const flags: { [key: string]: JSX.Element } = {
    CHF: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FF0000" />
        <rect x="13" y="6" width="6" height="20" fill="#FFFFFF" />
        <rect x="6" y="13" width="20" height="6" fill="#FFFFFF" />
      </svg>
    ),
    DKK: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#C8102E" />
        <rect x="10" y="0" width="3" height="32" fill="#FFFFFF" />
        <rect x="0" y="14.5" width="32" height="3" fill="#FFFFFF" />
      </svg>
    ),
    EUR: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#003399" />
        <g fill="#FFCC00">
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i * 30 - 90) * (Math.PI / 180);
            const x = 16 + 10 * Math.cos(angle);
            const y = 16 + 10 * Math.sin(angle);
            return (
              <g key={i} transform={`translate(${x},${y})`}>
                <path d="M 0,-1.2 L 0.35,-0.4 L 1.2,-0.4 L 0.5,0.15 L 0.75,1 L 0,0.4 L -0.75,1 L -0.5,0.15 L -1.2,-0.4 L -0.35,-0.4 Z" />
              </g>
            );
          })}
        </g>
      </svg>
    ),
    HUF: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="0" width="32" height="10.67" fill="#CD2A3E" />
        <rect y="21.33" width="32" height="10.67" fill="#436F4D" />
      </svg>
    ),
    PLN: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="16" width="32" height="16" fill="#DC143C" />
      </svg>
    ),
    SEK: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#006AA7" />
        <rect x="10" y="0" width="4" height="32" fill="#FECC00" />
        <rect x="0" y="14" width="32" height="4" fill="#FECC00" />
      </svg>
    ),
    UAH: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="0" width="32" height="16" fill="#005BBB" />
        <rect y="16" width="32" height="16" fill="#FFD500" />
      </svg>
    ),
    GBP: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#012169" />
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#FFFFFF" strokeWidth="5" />
        <path d="M0,0 L32,32 M32,0 L0,32" stroke="#C8102E" strokeWidth="3" />
        <rect x="13" y="0" width="6" height="32" fill="#FFFFFF" />
        <rect x="0" y="13" width="32" height="6" fill="#FFFFFF" />
        <rect x="14" y="0" width="4" height="32" fill="#C8102E" />
        <rect x="0" y="14" width="32" height="4" fill="#C8102E" />
      </svg>
    ),
    USD: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        {Array.from({ length: 7 }).map((_, i) => (
          <rect key={i} y={i * 4.57} width="32" height="2.46" fill="#B22234" />
        ))}
        <rect width="12.8" height="13.7" fill="#3C3B6E" />
        <g fill="#FFFFFF">
          {Array.from({ length: 50 }).map((_, i) => {
            const row = Math.floor(i / 6);
            const col = i % 6;
            const offsetX = row % 2 === 0 ? 0 : 1.3;
            return <circle key={i} cx={1.3 + col * 2.1 + offsetX} cy={1.3 + row * 1.5} r="0.4" />;
          })}
        </g>
      </svg>
    ),
    NOK: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#BA0C2F" />
        <rect x="10" y="0" width="5" height="32" fill="#FFFFFF" />
        <rect x="0" y="13.5" width="32" height="5" fill="#FFFFFF" />
        <rect x="11" y="0" width="3" height="32" fill="#00205B" />
        <rect x="0" y="14.5" width="32" height="3" fill="#00205B" />
      </svg>
    ),
    SKK: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="0" width="32" height="10.67" fill="#FFFFFF" />
        <rect y="10.67" width="32" height="10.67" fill="#0B4EA2" />
        <rect y="21.33" width="32" height="10.67" fill="#EE1C25" />
      </svg>
    ),
    BGN: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="0" width="32" height="10.67" fill="#FFFFFF" />
        <rect y="10.67" width="32" height="10.67" fill="#00966E" />
        <rect y="21.33" width="32" height="10.67" fill="#D62612" />
      </svg>
    ),
    RUB: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="0" width="32" height="10.67" fill="#FFFFFF" />
        <rect y="10.67" width="32" height="10.67" fill="#0039A6" />
        <rect y="21.33" width="32" height="10.67" fill="#D52B1E" />
      </svg>
    ),
    TRY: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#E30A17" />
        <circle cx="12" cy="16" r="6" fill="#FFFFFF" />
        <circle cx="14" cy="16" r="5" fill="#E30A17" />
        <g fill="#FFFFFF">
          <path d="M 18.5 11.5 L 21 16 L 18.5 20.5 L 17 18.2 L 18.5 16 L 17 13.8 Z" />
          <circle cx="18.5" cy="11.5" r="0.8" />
          <circle cx="21" cy="16" r="0.8" />
          <circle cx="18.5" cy="20.5" r="0.8" />
          <circle cx="17" cy="13.8" r="0.8" />
          <circle cx="17" cy="18.2" r="0.8" />
        </g>
      </svg>
    ),
    RON: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect x="0" y="0" width="10.67" height="32" fill="#002B7F" />
        <rect x="10.67" y="0" width="10.67" height="32" fill="#FCD116" />
        <rect x="21.33" y="0" width="10.67" height="32" fill="#CE1126" />
      </svg>
    ),
    HRK: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="0" width="32" height="10.67" fill="#FF0000" />
        <rect y="21.33" width="32" height="10.67" fill="#171796" />
      </svg>
    ),
    RSD: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <rect y="0" width="32" height="10.67" fill="#C6363C" />
        <rect y="10.67" width="32" height="10.67" fill="#0C4076" />
        <rect y="21.33" width="32" height="10.67" fill="#FFFFFF" />
      </svg>
    ),
    MKD: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#D20000" />
        <circle cx="16" cy="16" r="6" fill="#FFE600" />
        <g stroke="#FFE600" strokeWidth="3">
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = i * 45 * (Math.PI / 180);
            const x1 = 16 + 8 * Math.cos(angle);
            const y1 = 16 + 8 * Math.sin(angle);
            const x2 = 16 + 20 * Math.cos(angle);
            const y2 = 16 + 20 * Math.sin(angle);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
          })}
        </g>
      </svg>
    ),
    XDR: (
      <svg viewBox="0 0 32 32" className={className}>
        <rect width="32" height="32" fill="#FFFFFF" />
        <circle cx="16" cy="16" r="12" fill="none" stroke="#002664" strokeWidth="2" />
        <text x="16" y="20" textAnchor="middle" fontSize="10" fill="#002664" fontWeight="bold">
          SDR
        </text>
      </svg>
    ),
  };

  // Special case: XDR has no flag
  if (code === 'XDR') {
    return null;
  }

  return flags[code] || null;
};

export default FlagIcon;
